/* eslint-disable new-cap */
import axios from 'axios';
import _ from 'lodash';
import Promise from 'bluebird';
import { SubmissionError } from 'redux-form';
import { alerts } from 'modules/alerts/actions';

// Constants
import { DEFAULT_PAGE_SIZE } from 'constants';

// Switch to false if you do not want to log requests locally. Never log in production: the browser
// keeps a reference to every object handed to console.log, so logging each response pins its payload.
const logRequests = process.env.NODE_ENV !== 'production';

// Cancel sources for in-flight GETs, so they can all be cancelled on page change. Entries are removed
// as soon as their request settles — an unreleased source keeps the request's full response alive.
let cancelTokens = [];

function handleError(error) {
  const data = _.get(error, 'response.data');

  if (!data) throw error;

  if (_.includes(data.detail, 'Key (email)=') && _.includes(data.detail, 'already exists.')) {
    const email = _(data.detail).replace('Key (email)=(', '').replace(') already exists.', '');

    throw new SubmissionError({ _error: `Email ${email} is already in use!` });
  }

  if (data.detail) throw new SubmissionError({ _error: data.detail });

  alerts.catchError(error, { subtitle: data.message });

  throw error;
}

export default class Rest {
  constructor(data) {
    _.assign(this, data);
  }

  static find(query, options = {}) {
    const { path } = this;
    if (!options.cancelToken) options.cancelToken = this.getCancelToken(options);

    return Promise.resolve(axios.get(`/api/${path}`, _.defaults({ params: query }, options)))
      .tap(this.logRequest)
      .then((response) => response.data)
      .catch(axios.isCancel, _.noop)
      .finally(() => Rest.releaseCancelToken(options.cancelToken));
  }

  static findOne(index, params, options = {}) {
    const { path } = this;
    if (!options.cancelToken) options.cancelToken = this.getCancelToken(options);

    return Promise.resolve(axios.get(`/api/${path}/${index}`, _.defaults({ params }, options)))
      .tap(this.logRequest)
      .then((response) => response.data)
      .catch(axios.isCancel, _.noop)
      .finally(() => Rest.releaseCancelToken(options.cancelToken));
  }

  static create(item) {
    const { path } = this;

    return Promise.resolve(axios.post(`/api/${path}`, item))
      .tap(this.logRequest)
      .then((response) => response.data)
      .catch(handleError);
  }

  static update(item) {
    const { path } = this;

    return Promise.resolve(axios.put(`/api/${path}/${item.id}`, item))
      .tap(this.logRequest)
      .then((response) => response.data)
      .catch(handleError);
  }

  static updateMany(items) {
    const { path } = this;

    return Promise.resolve(axios.put(`/api/${path}`, items))
      .tap(this.logRequest)
      .then((response) => response.data)
      .catch(handleError);
  }

  static count(query, options = {}) {
    const { path } = this;
    if (!options.cancelToken) options.cancelToken = this.getCancelToken(options);

    return Promise.resolve(axios.get(`api/${path}/count`, _.defaults({ params: query }, options)))
      .tap(this.logRequest)
      .then((response) => Number(response.data.count))
      .catch(axios.isCancel, _.noop)
      .finally(() => Rest.releaseCancelToken(options.cancelToken));
  }

  static delete(id) {
    const { path } = this;

    return Promise.resolve(axios.delete(`/api/${path}/${id}`))
      .tap(this.logRequest);
  }

  static archive(id, options) {
    return this.update({ id, status: 'Archived' }, options);
  }

  static formatQuery(query = {}) {
    // Use omitBy isEmpty because empty filters will be set to empty string rather than undefined,
    // which will break filtering
    const filters = _.omitBy(query.where, value => _.isEmpty(value) && !_.isNumber(value)) || {};
    const limit = (this.defaultQuery || {}).limit || DEFAULT_PAGE_SIZE;
    const page = Number(query.page) || 1;

    const formattedQuery = {
      where: this.mapFilters ? this.mapFilters(filters) : filters,
      limit,
      offset: limit * (page - 1),
      sort: query.sort ? `${query.sort.field} ${query.sort.direction}` : undefined
    };

    return _.defaultsDeep(formattedQuery, this.defaultQuery);
  }

  // Only use this function for GET requests. All requests which are given a cancel token from this function
  // will be canceled on page change, and we should never be canceling PUT/POST/DELETE requests on page change
  static getCancelToken() {
    return this.getCancelSource().token;
  }

  // Like getCancelToken, but returns the whole source so the caller can also cancel it themselves.
  static getCancelSource() {
    const source = axios.CancelToken.source();
    // Add to cancelTokens so we can cancel it later
    cancelTokens.push(source);
    return source;
  }

  // Forget a token once its request has settled. Called for every token, including caller-supplied
  // ones that were never in the list, so it has to tolerate a miss.
  static releaseCancelToken(token) {
    if (!token) return;
    _.remove(cancelTokens, (source) => source.token === token);
  }

  static cancelAllCancellableRequests() {
    _.forEach(cancelTokens, (source) => {
      source.cancel();
    });
    cancelTokens = []; // Forget all previous cancel tokens
  }

  // Internal helper functions
  static logRequest(response) {
    if (logRequests) console.log(response);
  }
}
